// components/UserCard.tsx — dark-theme sidebar user block
import {
  CalendarClock,
  CreditCard,
  LogOut,
  ShieldAlert,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

interface UserCardProps {
  user: any;
  show: boolean;
  locale: string;
  isVerified: boolean;
  handleLogout: () => void;
}

export default function UserCard({ user, show, locale, isVerified, handleLogout }: UserCardProps) {
  const ar = locale === "ar";
  const ROLES: Record<string, string> = {
    developer: ar ? "مطور عقاري" : "Developer",
    bank: ar ? "حساب بنكي" : "Bank Account",
    agent: ar ? "وكيل عقاري" : "Agent",
    markter: ar ? "مسوق عقاري" : "Marketer",
  };
  const navigate = useRouter();
  const verified = user?.isVerified || isVerified;
  const accessBlocked = !!(user?.access?.requiresFee && !user?.access?.feePaid);

  const handleNavigate = () => {
    if (user?.role === "bank") return;
    navigate.push(`/${locale}/dashboard/profile`);
  };

  /* collapsed — avatar only */
  if (!show) {
    return (
      <div className="flex flex-col items-center gap-2">
        <button onClick={handleNavigate} className="relative">
          <Image src={user?.photo || "/user.webp"} alt="User" width={40} height={40} className="rounded-2xl ring-2 ring-white/10 object-cover" />
          <span className={`absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-[#0a1128] ${verified ? "bg-emerald-400" : "bg-amber-400"}`} />
        </button>
        <button onClick={handleLogout} title={ar ? "خروج" : "Sign out"} className="grid size-9 place-items-center rounded-xl text-rose-300/70 transition-colors hover:bg-rose-500/10 hover:text-rose-300">
          <LogOut size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-[22px] border border-white/[0.06] bg-white/[0.04] p-3.5">
      <div className="flex items-center gap-3">
        <button onClick={handleNavigate} className="relative shrink-0">
          <Image src={user?.photo || "/user.webp"} alt="User" width={44} height={44} className="rounded-2xl ring-2 ring-[#d9b25a]/30 object-cover" />
          <span className={`absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-[#0a1128] ${verified ? "bg-emerald-400" : "bg-amber-400"}`} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-black capitalize text-white">{user?.username || (ar ? "المستخدم" : "User")}</p>
          <p className="truncate text-[11px] font-bold text-[#d9b25a]/80">{ROLES[user?.role] || (ar ? "حساب المطور" : "Developer")}</p>
        </div>
      </div>

      {/* access fee state */}
      {accessBlocked && user?.role !== "bank" && (
        <Link href={`/${locale}/dashboard`} className="mt-3 flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 p-2.5 text-[11px] font-black text-amber-300 transition-colors hover:bg-amber-400/20">
          <CreditCard size={14} /> {ar ? "رسوم الوصول غير مدفوعة" : "Access fee unpaid"}
        </Link>
      )}

      {/* verification */}
      {user?.role !== "bank" && (
        <div className={`mt-3 rounded-xl border p-2.5 ${verified ? "border-emerald-400/20 bg-emerald-400/10" : "border-white/[0.06] bg-white/[0.03]"}`}>
          <div className={`flex items-center gap-2 text-[11px] font-black ${verified ? "text-emerald-300" : "text-white/60"}`}>
            {verified ? <ShieldCheck size={14} /> : <ShieldAlert size={14} className="text-amber-300" />}
            {verified ? (ar ? "حساب موثق" : "Verified") : ar ? "حساب غير موثق" : "Unverified"}
          </div>
          {verified ? (
            user?.endsAt && (
              <p className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-emerald-200/70">
                <CalendarClock size={11} /> {ar ? `ينتهي ${user.endsAt}` : `Expires ${user.endsAt}`}
              </p>
            )
          ) : (
            <Link href={`/${locale}/dashboard/verification-account`} className="mt-2 flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-br from-[#d9b25a] to-[#b8923f] py-1.5 text-[11px] font-black text-[#0a1128] transition-all hover:brightness-110">
              <UserPlus size={13} /> {ar ? "وثّق حسابك" : "Verify now"}
            </Link>
          )}
        </div>
      )}

      <button onClick={handleLogout} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-rose-400/15 bg-rose-500/[0.08] py-2.5 text-xs font-black text-rose-300/80 transition-all hover:bg-rose-500/20 hover:text-rose-200">
        <LogOut size={15} /> {ar ? "تسجيل الخروج" : "Sign out"}
      </button>
    </div>
  );
}
